import React, { useState } from "react";
import Control from "../../../assets/vfx/controls/control";
import { GRID_ACTION_TYPES } from "../../../assets/types";
import {
    createActionColumn,
    createLayoutOptions,
} from "../../../assets/configuration/gridConfig";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { IControlStyles, useControlStyles } from "../../../assets/layouts";
import { LoadingIndicator } from "../../../assets/vfx";
import { CommunityDevelopmentService } from "./communityDevelopmentService";
import { CommunityDevelopmentNoteDto, SearchDto } from "../../../services/dto";
import { SearchOperator, SortDirection, SortModel } from "../../../assets/utils/lib";
import { FormatHelper } from "../../../assets/utils/helpers";

interface CommunityDevelopmentNotesGridProps {
    communityDevelopmentId: string;
    onNotesCustomEvent?: (actionType: string, dataId: string) => void;
}

const notesSortModel: SortModel = {
    field: "submittedDate",
    direction: SortDirection.Descending,
};

const DATA_ID_FIELD = "id";

const createGridColumns = (): ControlDataGridColumnProps[] => [
    {
        data: DATA_ID_FIELD,
        title: "ID",
        visible: false,
        columnType: "number",
        alignment: "right",
        sortable: false
    },
    {
        data: "notes",
        title: "Notes",
        visible: true,
        columnType: "text",
        alignment: "left",
        sortable: false,
        width: "40%",
    },
    {
        data: "submittedDate",
        title: "Submitted Date",
        visible: true,
        columnType: "custom",
        alignment: "left",
        width: "18%",
        sortable: true,
        customCell: (id: string, rowData: CommunityDevelopmentNoteDto) => {
            if (rowData.submittedDate) {
                return FormatHelper.formatUtcLocalDate(rowData.submittedDate)
            }
            return null
        },
    },
    {
        data: "submittedBy",
        title: "Submitted By",
        visible: true,
        columnType: "text",
        alignment: "left",
        width: "18%",
        sortable: false,
    },
];

export const CommunityDevelopmentNotesGrid = (
    props: CommunityDevelopmentNotesGridProps
) => {
    const styles: IControlStyles = useControlStyles();
    const notesSearchQuery: SearchDto = {
        pageNumber: 1,
        pageSize: 10,
        sorts: [notesSortModel],
        filters: [
            {
                field: 'communityDevelopmentId',
                operator: SearchOperator.Equal,
                value: props.communityDevelopmentId,
            },
            {
                field: 'isDeleted',
                operator: SearchOperator.IsFalse,
                value: false,
            }
        ],
    };

    const [searchQuery, setSearchQuery] = useState<SearchDto>(notesSearchQuery);


    const { data, isLoading } =
        CommunityDevelopmentService.getCommunityDevelopmentNotesQuery(
            searchQuery
        );

    const notes: CommunityDevelopmentNoteDto[] = data?.result ?? [];
    const hasData: boolean = notes.length > 0;
    const gridColumns: ControlDataGridColumnProps[] = createGridColumns();
    const actionColumn = createActionColumn(
        styles,
        DATA_ID_FIELD,
        [GRID_ACTION_TYPES.EDIT, GRID_ACTION_TYPES.DELETE],
        true,
        "right"
    );
    const layoutOptions = createLayoutOptions(hasData);

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Control.DataGrid
            key={JSON.stringify(layoutOptions)}
            dataId={DATA_ID_FIELD}
            cols={gridColumns}
            data={notes}
            actionCol={actionColumn}
            layoutOptions={layoutOptions}
            totalRecords={data?.totalSize ?? (data?.result?.length ?? 0)}
            query={searchQuery}
            onCustomEvent={props.onNotesCustomEvent}
            onQueryChange={(patch) =>
                setSearchQuery((query) => ({ ...query, ...patch, sorts: patch.sortBy ? [patch.sortBy] : query.sorts }))
            }
            behaviorOptions={{ responsive: false }}
        />
    );
};
